import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight, Loader2, AlertTriangle } from "lucide-react";

// Tenancy Timeline entry card — FREE, shown in the "During your tenancy" tab.
// Chronological overview across the whole tenancy (move-in, during, move-out).
export function TenancyTimelineCard({ propertyId }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error

  const load = () => {
    setState("loading");
    api.get(`/properties/${propertyId}/tenancy-timeline`)
      .then((r) => { setD(r.data); setState("ok"); })
      .catch(() => setState("error"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const Shell = ({ children, testid }) => (
    <div data-testid={testid} className="rounded-2xl border border-[#E2E8F0] bg-white p-5 theme-card">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><Clock size={20} /></span>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );

  if (state === "loading") return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 flex items-center justify-center theme-card" data-testid="tenancy-timeline-card-loading">
      <Loader2 className="animate-spin text-brand" size={18} />
    </div>
  );

  if (state === "error") return (
    <Shell testid="tenancy-timeline-card-error">
      <h3 className="font-heading text-lg font-bold text-navy">Tenancy Timeline</h3>
      <p className="mt-0.5 text-sm text-[#64748B] flex items-center gap-1.5"><AlertTriangle size={14} className="text-amber-500" /> We couldn't load your timeline right now.</p>
      <Button data-testid="tenancy-timeline-retry" variant="outline" onClick={load} className="mt-3 h-10 rounded-xl border-[#E2E8F0] text-navy">Retry</Button>
    </Shell>
  );

  const count = d?.event_count || 0;
  return (
    <Shell testid="tenancy-timeline-card">
      <h3 className="font-heading text-lg font-bold text-navy">Tenancy Timeline</h3>
      <p className="mt-0.5 text-sm text-[#64748B] leading-snug">
        Everything you documented, in order — from move-in through your tenancy to move-out.
      </p>
      {count > 0 ? (
        <p className="mt-2 text-sm font-semibold text-navy" data-testid="tenancy-timeline-card-summary">
          {count} entr{count === 1 ? "y" : "ies"} on your timeline
        </p>
      ) : (
        <p className="mt-2 text-sm text-[#64748B]" data-testid="tenancy-timeline-card-empty">Nothing recorded yet — it fills in as you document.</p>
      )}
      <Button data-testid="tenancy-timeline-open" onClick={() => navigate(`/tenancy-timeline/${propertyId}`)}
        className="mt-3 h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
        View Tenancy Timeline <ArrowRight size={15} className="ml-1.5" />
      </Button>
    </Shell>
  );
}
